import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PopoverPosition } from '@mui/material';
import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChevronDown, ChevronLeft, ChevronRight, CirclePlus, Pin, X } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { ScrollButtonController } from 'src/components/generic/scrollable/controllers/ScrollButtonController';
import type { Hotkey } from 'src/helpers/hotkeys';
import { useHorizontalScrollEdges, useOnChange, useOverrideGlobalCursor } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { tabHotkeys, useTabContext } from './tab-context';
import { TabContextMenu } from './tab-context-menu';
import { TabIcon } from './tab-icon';
import { TabOverflowMenu } from './tab-overflow-menu';
import type { Tab } from './tab-types';
import { TAB_TYPES } from './tab-types';
import { useIsActiveTabFocused } from '../focused-view';

export const TAB_PANEL_ID = 'tab-panel';

const TAB_LIST_ID = 'tab-list';

/**
 * How far a keyboard activation of a scroll button scrolls the tab list. Pointer presses are
 * handled by ScrollButtonController instead, which scrolls continuously while held.
 */
const KEYBOARD_SCROLL_DISTANCE = 160;

/**
 * Size of the icon buttons that sit alongside the tab list, and the gap between them.
 *
 * These satisfy WCAG 2.2 SC 2.5.8 through its spacing exception rather than by size: the
 * buttons are under 24px, so instead the centre of each must sit at least 24px from the centre
 * of every other target, and at least 12px from the edge of one. An 18px button with a 6px gap
 * puts adjacent centres exactly 24px apart, and each centre 15px from the nearest tab's edge.
 * Changing either value without redoing that arithmetic will break the spacing exception.
 */
const CONTROL_BUTTON_SIZE = 18;
const CONTROL_BUTTON_GAP = 6;

const useStyles = makeStyles()(theme => ({
  addButton: {
    '&:hover': {
      color: theme.appColors.primary,
    },
    alignItems: 'center',
    alignSelf: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    flexShrink: 0,
    height: CONTROL_BUTTON_SIZE,
    justifyContent: 'center',
    // Wider than CONTROL_BUTTON_GAP, which only widens the gap to the button before it.
    marginLeft: theme.spacing(1),
    minWidth: 0,
    padding: 0,
    width: CONTROL_BUTTON_SIZE,
  },
  closeButton: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    alignItems: 'center',
    borderRadius: '50%',
    color: 'inherit',
    display: 'flex',
    flexShrink: 0,
    height: 18,
    justifyContent: 'center',
    opacity: 0,
    padding: 0,
    width: 18,
  },
  overflowButton: {
    '&:hover': {
      color: theme.appColors.primary,
    },
    alignItems: 'center',
    alignSelf: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    flexShrink: 0,
    height: CONTROL_BUTTON_SIZE,
    justifyContent: 'center',
    marginLeft: CONTROL_BUTTON_GAP,
    minWidth: 0,
    padding: 0,
    width: CONTROL_BUTTON_SIZE,
  },
  pinnedIndicator: {
    alignItems: 'center',
    color: 'inherit',
    display: 'flex',
    flexShrink: 0,
  },
  scrollButton: {
    '&.Mui-disabled': {
      opacity: 0.3,
    },
    '&:hover': {
      color: theme.appColors.primary,
    },
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    height: CONTROL_BUTTON_SIZE,
    justifyContent: 'center',
    minWidth: 0,
    padding: 0,
    width: CONTROL_BUTTON_SIZE,
  },
  /**
   * The button is wrapped so the tooltip has an enabled element to listen on: a disabled
   * button fires no pointer events, and MUI warns when one is a tooltip's direct child.
   */
  scrollButtonWrapper: {
    alignSelf: 'center',
    display: 'flex',
    flexShrink: 0,
  },
  scrollButtonWrapperLeft: {
    marginRight: CONTROL_BUTTON_GAP,
  },
  scrollButtonWrapperRight: {
    marginLeft: CONTROL_BUTTON_GAP,
  },
  tabBar: {
    alignItems: 'flex-end',
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 0',
    flexShrink: 1,
    height: '100%',
    minWidth: 0,
    padding: theme.spacing(0, 0.5, 0, 0),
  },
  tabDivider: {
    backgroundColor: theme.appColors.rule,
    flexShrink: 0,
    height: 20,
    marginBottom: 'auto',
    marginTop: 'auto',
    width: 1,
  },
  tabIcon: {
    display: 'flex',
    flexShrink: 0,
  },
  tabItem: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
    '&:hover $closeButton': {
      opacity: 1,
    },
    alignItems: 'center',
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.75),
    height: '100%',
    maxWidth: 280,
    minWidth: 80,
    padding: theme.spacing(0, 1),
    position: 'relative',
    textTransform: 'none',
    transition: 'color 0.15s ease',
    userSelect: 'none',
  },
  tabItemActive: {
    '&::after': {
      backgroundColor: theme.appColors.primary,
      borderRadius: '2px 2px 0 0',
      bottom: 0,
      content: '""',
      height: 3,
      left: 0,
      position: 'absolute',
      right: 0,
    },
    '&:hover': {
      backgroundColor: 'rgba(66, 133, 244, 0.06)',
    },
    backgroundColor: 'rgba(66, 133, 244, 0.06)',
    color: theme.appColors.primary,
  },
  tabLabel: {
    '&::after': {
      content: 'attr(data-text)',
      display: 'block',
      fontWeight: 600,
      height: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      visibility: 'hidden',
    },
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  tabLabelActive: {
    fontWeight: 600,
  },
  tabList: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    alignItems: 'flex-end',
    display: 'flex',
    height: '100%',
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
  },
}));

interface TabItemProps {
  /** The index of the tab in the tab bar. */
  index: number;
  isActive: boolean;
  /** Whether the tab is the last in the tab bar. */
  isLast: boolean;
  /** Whether the previous tab is the active tab. */
  isNext: boolean;
  /** Whether the next tab is the active tab. */
  isPrevious: boolean;
  onClose: (tabId: UUID) => void;
  onContextMenu: (tabId: UUID, position: PopoverPosition) => void;
  onSwitch: (tabId: UUID) => void;
  tab: Tab;
}

const TabItem = React.memo(function TabItem({
  index,
  isActive,
  isLast,
  isNext,
  isPrevious,
  onClose,
  onContextMenu,
  onSwitch,
  tab,
}: TabItemProps): JSX.Element {
  const { classes } = useStyles();
  const [hovered, setHovered] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  /**
   * Tooltip truncation detection. Pinning changes the label's available width
   * (it adds the pin icon and drops the close button), so re-measure on that
   * too — not just when the title changes.
   */
  const [isTruncated, setIsTruncated] = useState(false);
  useEffect(() => {
    const el = labelRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [tab.pinned, tab.title]);

  // dnd-kit sortable
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: tab.id,
  });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const hotkeyHint = useMemo(() => {
    if (isActive) return null;

    let hotkey: Hotkey<never> | null = null;
    let hotkeyDescription: string | null = null;

    if (isPrevious) {
      hotkey = tabHotkeys.previousTab;
      hotkeyDescription = 'Previous tab';
    } else if (isNext) {
      hotkey = tabHotkeys.nextTab;
      hotkeyDescription = 'Next tab';
    } else if (index <= 7) {
      hotkey = tabHotkeys.switchToTab;
      hotkeyDescription = 'Switch to tab';
    } else if (isLast) {
      hotkey = tabHotkeys.switchToLastTab;
      hotkeyDescription = 'Last tab';
    }

    return hotkey?.hint && hotkeyDescription ? (
      <>
        {hotkeyDescription} <kbd>{hotkey.hint.replace('<N>', (index + 1).toString())}</kbd>
      </>
    ) : null;
  }, [index, isActive, isLast, isNext, isPrevious]);

  const tooltipTitle = useMemo(() => {
    if (isTruncated && hotkeyHint)
      return (
        <>
          {tab.title}
          <br />
          {hotkeyHint}
        </>
      );
    if (isTruncated) return tab.title;
    if (hotkeyHint) return hotkeyHint;
    return null;
  }, [hotkeyHint, isTruncated, tab.title]);

  const shouldShowTooltip = !!tooltipTitle && !isDragging;

  const handleClick = useCallback(() => {
    setTooltipOpen(false);
    onSwitch(tab.id);
  }, [onSwitch, tab.id]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTooltipOpen(false);
      onClose(tab.id);
    },
    [onClose, tab.id]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Middle-click close (skip pinned)
      if (e.button === 1) {
        e.preventDefault();
        setTooltipOpen(false);
        if (!tab.pinned) onClose(tab.id);
      }
    },
    [onClose, tab.id, tab.pinned]
  );

  const handleRightClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setTooltipOpen(false);
      onContextMenu(tab.id, { left: e.clientX, top: e.clientY });
    },
    [onContextMenu, tab.id]
  );

  useOnChange(() => setTooltipOpen(false), [isActive, isDragging, tab.title]);

  useOnChange(() => {
    if (!shouldShowTooltip) setTooltipOpen(false);
  }, [shouldShowTooltip]);

  return (
    <div
      ref={setNodeRef}
      data-tab-id={tab.id}
      style={style}
      {...attributes}
      {...listeners}
      aria-controls={TAB_PANEL_ID}
      aria-label={tab.title}
      aria-selected={isActive}
      className={clsx(classes.tabItem, {
        [classes.tabItemActive]: isActive,
      })}
      role="tab"
      onClick={handleClick}
      onContextMenu={handleRightClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setTooltipOpen(false);
      }}
    >
      {/* The pin replaces the type icon rather than sitting beside it — two icons read as unbalanced. */}
      {tab.pinned ? (
        <span className={classes.pinnedIndicator} title="Pinned tab">
          <Pin size={18} strokeWidth={1.5} />
        </span>
      ) : (
        <span className={classes.tabIcon}>
          <TabIcon type={tab.type} />
        </span>
      )}

      <AlineTooltip
        arrow
        describeChild
        disableInteractive
        disableHoverListener={!shouldShowTooltip}
        open={tooltipOpen}
        placement="top"
        title={tooltipTitle}
        onClose={() => setTooltipOpen(false)}
        onOpen={() => {
          if (shouldShowTooltip) setTooltipOpen(true);
        }}
      >
        <Typography
          ref={labelRef}
          className={clsx(classes.tabLabel, isActive && classes.tabLabelActive)}
          component="span"
          data-text={tab.title}
        >
          {tab.title}
        </Typography>
      </AlineTooltip>

      {!tab.pinned && (
        <AlineTooltip
          arrow
          title={
            <>
              Close tab
              {isActive && (
                <>
                  {' '}
                  <kbd>{tabHotkeys.closeTab.hint}</kbd>
                </>
              )}
            </>
          }
        >
          <ButtonBase
            aria-label="Close tab"
            className={classes.closeButton}
            style={{ opacity: hovered ? 1 : 0 }}
            onClick={handleClose}
          >
            <X size={12} strokeWidth={2} />
          </ButtonBase>
        </AlineTooltip>
      )}
    </div>
  );
});

export function TabBar(): JSX.Element {
  const { classes } = useStyles();
  const {
    activeTab,
    internalMethods: {
      closeOtherTabs,
      closeTab,
      closeTabsToLeft,
      closeTabsToRight,
      openNewTab,
      setTabOrder,
      switchToTab,
      togglePinTab,
    },
    tabs,
  } = useTabContext();
  const isFocused = useIsActiveTabFocused();
  const tabListRef = useRef<HTMLDivElement>(null);

  const activeTabIndex = useMemo(
    () => tabs.findIndex(tab => tab.id === activeTab?.id),
    [activeTab?.id, tabs]
  );

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    position: PopoverPosition;
    tab: Tab;
  } | null>(null);

  // Active drag state for DragOverlay
  const [activeDragId, setActiveDragId] = useState<UUID | null>(null);
  const activeDragTab = activeDragId ? (tabs.find(t => t.id === activeDragId) ?? null) : null;

  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);

  /**
   * Whether the tabs overflow, and whether there is anything to scroll to in each direction.
   * The scroll buttons are shown while overflowing and disabled once their edge is reached.
   */
  const { canScrollLeft, canScrollRight, isOverflowing } = useHorizontalScrollEdges(tabListRef);

  /** Scroll a tab into view, measured a frame later so the layout that prompted it has settled. */
  const revealTab = useCallback((id: UUID) => {
    requestAnimationFrame(() => {
      const list = tabListRef.current;
      const el = list?.querySelector<HTMLElement>(`[data-tab-id="${id}"]`);
      if (!list || !el) return;

      const listRect = list.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      if (elRect.left < listRect.left) {
        list.scrollBy({ behavior: 'smooth', left: elRect.left - listRect.left });
      } else if (elRect.right > listRect.right) {
        list.scrollBy({ behavior: 'smooth', left: elRect.right - listRect.right });
      }
    });
  }, []);

  /**
   * Auto-scroll the active tab into view. Debounced (trailing-only) so that
   * rapid-fire triggers — active id change → async title arrives → overflow
   * chevron mounts — coalesce into a single smooth scroll instead of jumping.
   */
  const scrollActiveTabIntoView = useDebouncedCallback(revealTab, 500);

  useEffect(() => {
    const id = activeTab?.id;
    if (!id || !activeTab?.title) return;
    scrollActiveTabIntoView(id);
  }, [activeTab?.id, activeTab?.title, tabs.length, isOverflowing, scrollActiveTabIntoView]);

  /**
   * Reveal the active tab when it changes position rather than identity. Pinning moves it to
   * the front of a bar that is scrolled somewhere else, and none of the debounced effect's
   * inputs change on a reorder — the tab was only ever revealed because a debounce started by
   * the preceding tab switch happened to land after the reorder, which is a race rather than a
   * guarantee. Any pending reveal is cancelled first: it was measured against the old order and
   * would scroll somewhere else.
   *
   * This one is not debounced. Coalescing exists for the burst when a tab opens, whereas a
   * reorder is a single deliberate action, and half a second of the tab sitting off screen
   * afterwards reads as the bar having lost it.
   */
  const previousActiveRef = useRef({ id: activeTab?.id, index: activeTabIndex });

  useEffect(() => {
    const previous = previousActiveRef.current;
    const id = activeTab?.id;
    previousActiveRef.current = { id, index: activeTabIndex };

    // A switch to a different tab is the debounced effect's job; this is only for a move.
    if (!id || id !== previous.id) return;
    if (activeTabIndex < 0 || activeTabIndex === previous.index) return;

    scrollActiveTabIntoView.cancel();
    revealTab(id);
  }, [activeTabIndex, activeTab?.id, revealTab, scrollActiveTabIntoView]);

  const scrollLeftButtonRef = useRef<HTMLButtonElement>(null);
  const scrollRightButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Drive the scroll buttons with the same controller the scrollbar arrows use: a short scroll
   * on pointer down, then smooth continuous scrolling for as long as the button is held. The
   * buttons only exist while the tabs overflow, so the refs are re-read when that changes.
   */
  useEffect(() => {
    const scrollable = tabListRef.current;
    const scrollLeftButton = scrollLeftButtonRef.current;
    const scrollRightButton = scrollRightButtonRef.current;
    if (!scrollable || !scrollLeftButton || !scrollRightButton) return;

    const disconnectScrollLeft = new ScrollButtonController({
      button: scrollLeftButton,
      direction: 'horizontal',
      scrollable,
      sign: -1,
    }).connect();

    const disconnectScrollRight = new ScrollButtonController({
      button: scrollRightButton,
      direction: 'horizontal',
      scrollable,
      sign: 1,
    }).connect();

    return () => {
      disconnectScrollLeft();
      disconnectScrollRight();
    };
  }, [isOverflowing]);

  /**
   * Keyboard activations of a scroll button produce a click with no preceding pointer press,
   * which the controller never sees, so scroll a fixed distance for those. A `detail` of 0
   * distinguishes them from the clicks that follow a pointer press.
   */
  const handleScrollButtonClick = useCallback((event: React.MouseEvent, sign: -1 | 1) => {
    if (event.detail !== 0) return;
    tabListRef.current?.scrollBy({ behavior: 'smooth', left: sign * KEYBOARD_SCROLL_DISTANCE });
  }, []);

  // dnd-kit sensors — distance threshold prevents accidental drags on click
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const fromIndex = tabs.findIndex(t => t.id === active.id);
      const toIndex = tabs.findIndex(t => t.id === over.id);
      if (fromIndex === -1 || toIndex === -1) return;

      // Block cross-boundary drags (pinned <-> unpinned)
      if (tabs[fromIndex].pinned !== tabs[toIndex].pinned) return;

      const reordered = [...tabs];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      setTabOrder(reordered.map(t => t.id));
    },
    [tabs, setTabOrder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  const handleAddTab = useCallback(() => {
    openNewTab(TAB_TYPES.home, 'Home', '/chat');
  }, [openNewTab]);

  const handleContextMenu = useCallback(
    (tabId: UUID, position: PopoverPosition) => {
      const tab = tabs.find(t => t.id === tabId);
      if (tab) setContextMenu({ position, tab });
    },
    [tabs]
  );

  return (
    <div aria-label="Tab bar" className={classes.tabBar} role="tablist">
      {isOverflowing && (
        <AlineTooltip arrow placement="top" title="Scroll tabs left">
          <span className={clsx(classes.scrollButtonWrapper, classes.scrollButtonWrapperLeft)}>
            <ButtonBase
              ref={scrollLeftButtonRef}
              aria-controls={TAB_LIST_ID}
              aria-label="Scroll tabs left"
              className={classes.scrollButton}
              disabled={!canScrollLeft}
              onClick={e => handleScrollButtonClick(e, -1)}
            >
              <ChevronLeft
                height={CONTROL_BUTTON_SIZE}
                strokeWidth={1.5}
                width={CONTROL_BUTTON_SIZE}
              />
            </ButtonBase>
          </span>
        </AlineTooltip>
      )}

      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
        sensors={sensors}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <SortableContext items={tabs.map(t => t.id)} strategy={horizontalListSortingStrategy}>
          <div ref={tabListRef} className={classes.tabList} id={TAB_LIST_ID}>
            {tabs.map((tab, index) => (
              <React.Fragment key={tab.id}>
                {index > 0 && <div className={classes.tabDivider} />}
                <TabItem
                  index={index}
                  isActive={tab.id === activeTab?.id}
                  isLast={index === tabs.length - 1}
                  isNext={activeTabIndex > -1 && index === activeTabIndex + 1}
                  isPrevious={activeTabIndex > -1 && index === activeTabIndex - 1}
                  tab={tab}
                  onClose={closeTab}
                  onContextMenu={handleContextMenu}
                  onSwitch={switchToTab}
                />
              </React.Fragment>
            ))}
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeDragTab ? (
            <TabItemOverlay isActive={activeDragTab.id === activeTab?.id} tab={activeDragTab} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {isOverflowing && (
        <AlineTooltip arrow placement="top" title="Scroll tabs right">
          <span className={clsx(classes.scrollButtonWrapper, classes.scrollButtonWrapperRight)}>
            <ButtonBase
              ref={scrollRightButtonRef}
              aria-controls={TAB_LIST_ID}
              aria-label="Scroll tabs right"
              className={classes.scrollButton}
              disabled={!canScrollRight}
              onClick={e => handleScrollButtonClick(e, 1)}
            >
              <ChevronRight
                height={CONTROL_BUTTON_SIZE}
                strokeWidth={1.5}
                width={CONTROL_BUTTON_SIZE}
              />
            </ButtonBase>
          </span>
        </AlineTooltip>
      )}
      {isOverflowing && (
        <AlineTooltip arrow placement="top" title="All tabs">
          <ButtonBase
            aria-label="All tabs"
            className={classes.overflowButton}
            onClick={e => setOverflowAnchor(e.currentTarget)}
          >
            <ChevronDown
              height={CONTROL_BUTTON_SIZE}
              strokeWidth={1.5}
              width={CONTROL_BUTTON_SIZE}
            />
          </ButtonBase>
        </AlineTooltip>
      )}

      {!isFocused && (
        <AlineTooltip
          arrow
          placement="top"
          title={
            <>
              Add new tab <kbd>{tabHotkeys.openNewTab.hint}</kbd>
            </>
          }
        >
          <ButtonBase aria-label="Add new tab" className={classes.addButton} onClick={handleAddTab}>
            <CirclePlus
              height={CONTROL_BUTTON_SIZE}
              strokeWidth={1.5}
              width={CONTROL_BUTTON_SIZE}
            />
          </ButtonBase>
        </AlineTooltip>
      )}

      <TabContextMenu
        anchorPosition={contextMenu?.position ?? null}
        tab={contextMenu?.tab ?? null}
        tabs={tabs}
        onClose={() => setContextMenu(null)}
        onCloseOtherTabs={closeOtherTabs}
        onCloseTab={closeTab}
        onCloseTabsToLeft={closeTabsToLeft}
        onCloseTabsToRight={closeTabsToRight}
        onTogglePin={togglePinTab}
      />

      <TabOverflowMenu
        activeTabId={activeTab?.id ?? null}
        anchorEl={overflowAnchor}
        tabs={tabs}
        onClose={() => setOverflowAnchor(null)}
        onSwitchToTab={switchToTab}
      />
    </div>
  );
}

/**
 * Static overlay shown under the cursor while dragging a tab.
 * This is rendered inside <DragOverlay> and does NOT use useSortable —
 * it's just a visual clone so the dragged tab keeps its original size.
 */
interface TabItemOverlayProps {
  isActive: boolean;
  tab: Tab;
}

function TabItemOverlay({ isActive, tab }: TabItemOverlayProps): JSX.Element {
  const { classes } = useStyles();

  const overlayStyle: React.CSSProperties = {
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    pointerEvents: 'none',
  };

  useOverrideGlobalCursor('grabbing');

  return (
    <div
      className={clsx(classes.tabItem, {
        [classes.tabItemActive]: isActive,
      })}
      style={overlayStyle}
    >
      {/* The pin replaces the type icon rather than sitting beside it — two icons read as unbalanced. */}
      {tab.pinned ? (
        <span className={classes.pinnedIndicator} title="Pinned tab">
          <Pin size={18} strokeWidth={1.5} />
        </span>
      ) : (
        <span className={classes.tabIcon}>
          <TabIcon type={tab.type} />
        </span>
      )}

      <Typography
        className={clsx(classes.tabLabel, isActive && classes.tabLabelActive)}
        component="span"
        data-text={tab.title}
      >
        {tab.title}
      </Typography>
    </div>
  );
}
